// 腾讯云 COS 基础 URL（构建期只引用，不下载）
const COS_URL = 'https://zixiancao-1306805299.cos.ap-guangzhou.myqcloud.com';

// Logo images
export const logo = `${COS_URL}/images/logo.png`;
export const logoWhite = `${COS_URL}/images/logo-white.png`;

// 产品的公开事实只在 productFacts 里维护一份，这里派生列表卡片用的字段。
import { findProductFacts, productCategoryList, productFacts } from './productFacts.js';

// 价格、认证、疗效、销量、年限、投资额等未经核实的断言已从活跃页面移除，
// 待核项见 docs/fact-check-backlog.md；逐 SKU 的公开字段来源见 docs/product-geo/。
const PACKAGING_NOTE = '本页参数照录自该 SKU 现行包装标签；不同批次包装细节可能不同，请以收到的实物包装标示为准。';

/**
 * @typedef {Object} ProductCopy
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} [image] 用于卡片与结构化数据的主图；无图 SKU 不出图也不进 JSON-LD image
 * @property {string[]} features
 * @property {string[]} spec 卡片上要直接看到的净含量与袋数
 * @property {string} ingredients 配料，卡片直接展示
 * @property {string} usage
 * @property {string} packagingNote
 * @property {string} categories
 */

/**
 * @param {import('./productFacts.js').ProductFacts} facts
 * @returns {ProductCopy}
 */
function toCardCopy(facts) {
  return {
    id: facts.id,
    title: facts.shortName,
    description: facts.lead,
    image: facts.images.length > 0 ? facts.images[0].src : undefined,
    features: [`${facts.spec[0]}`, ...facts.highlights.slice(0, 2)],
    spec: facts.spec,
    ingredients: facts.ingredients,
    usage: facts.method,
    packagingNote: PACKAGING_NOTE,
    shelfLife: facts.shelfLife,
    storage: facts.storage
  };
}

/** @type {Record<string, ProductCopy[]>} */
export const products = productCategoryList.reduce((byCategory, category) => {
  byCategory[category.id] = productFacts
    .filter((facts) => facts.category === category.id)
    .map(toCardCopy);
  return byCategory;
}, {});

// 原有分类保留名字，避免旧链接与文案引用失效；没有包装资料可公开的分类不进共享目录，
// 也不发空壳卡片，理由见 docs/product-geo/sources-conflicts.md。
const RETIRED_CATEGORIES = [
  { id: 'sanqi', name: '三七系列' },
  { id: 'lingzhi', name: '灵芝系列' },
  { id: 'jingyou', name: '精油系列' }
];

export const productCategories = [...productCategoryList, ...RETIRED_CATEGORIES];

/**
 * 共享产品目录：首页与匠心产品页共用这一份顺序与分类，空分类不出现。
 * 卡片字段全部由 productFacts 派生，两处入口不再各自维护主文案。
 * @type {{ id: string, name: string, items: ProductCopy[] }[]}
 */
export const productCatalog = productCategoryList
  .map((category) => ({ ...category, items: productFacts.filter((facts) => facts.category === category.id).map(toCardCopy) }))
  .filter((category) => category.items.length > 0);

// 每个有公开资料的 SKU 都有独立详情页。
export const showcasedProductIds = productFacts.filter((facts) => facts.detailPage).map((facts) => facts.id);

/**
 * @param {string} id
 * @returns {ProductCopy | null}
 */
export function findProductById(id) {
  if (!findProductFacts(id)) return null;
  for (const list of Object.values(products)) {
    const found = list.find((product) => product.id === id);
    if (found) return found;
  }
  return null;
}

/**
 * @param {string} id
 * @returns {string}
 */
export function productDetailHref(id) {
  return `/products/${id}.html`;
}

export default {
  logo,
  logoWhite,
  products,
  productCatalog,
  productCategories,
  showcasedProductIds,
  productDetailHref,
  findProductById
};
